import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
    Alert,
    Box,
    Chip,
    Container,
    Divider,
    Grid2 as Grid,
    LinearProgress,
    Link,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListSubheader,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { resolveApiBaseURL } from "../config/apiHost";

const POLL_MS = 30_000;

type CheckStatus = "ok" | "error" | "warning" | "unknown" | "degraded";


interface HealthCheckItem {
    label: string;
    status: CheckStatus;
    detail: string;
    last_run_at?: string;
    age_seconds?: number;
}

interface HealthPayload {
    timestamp?: string;
    overall: CheckStatus;
    checks: HealthCheckItem[]
}


/** Sample simulator recording for the E2E card (VTT captions + JSON step index). */
const E2E_AUDIT_VIDEO_URL =
    "https://storage.googleapis.com/strongmsp-media/media/simulator/assignment_279_athlete_362_steps_1-55_20260322_194428.webm";

const E2E_AUDIT_VIDEO_SRT_URL =
    "https://storage.googleapis.com/strongmsp-media/media/simulator/simulator_log_2026-03-22__279_captions.srt";
/** Same run as captions; expects `steps[]` with `title`, `role`, `start_sec`, optional `stage`, `end_sec`. */
const E2E_AUDIT_VIDEO_JSON_URL =
    "https://storage.googleapis.com/strongmsp-media/media/simulator/simulator_log_2026-03-22__279.json";

interface AuditVideoStepSegment {
    title: string;
    /** Flow section from simulator JSON (e.g. Pre-Assessment). */
    stage?: string;
    /** Simulator persona for this step (e.g. coach, athlete). */
    role?: string;
    start_sec: number;
    end_sec: number | null;
}

const MIN_AUDIT_CUE_SEC = 0.05;

function formatSegmentTitleWithRole(role: string | undefined, title: string): string {
    const r = role?.trim();
    if (!r) {
        return title;
    }
    const label = r.charAt(0).toUpperCase() + r.slice(1).toLowerCase();
    return `${label} · ${title}`;
}

function vttEscape(text: string): string {
    return text.replace(/&/g, "&amp;").replace(/</g, "&lt;");
}

/** Match `simulator_log_to_captions` timestamp formatting. */
function vttTimestamp(seconds: number): string {
    let s = Number.isFinite(seconds) && seconds >= 0 ? seconds : 0;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    s = s % 60;
    let whole = Math.floor(s);
    let ms = Math.round((s - whole) * 1000);
    if (ms >= 1000) {
        whole += 1;
        ms = 0;
    }
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(whole).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
}

function buildWebVttFromSegments(segments: AuditVideoStepSegment[]): string {
    const lines = ["WEBVTT", ""];
    let idx = 0;
    for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];
        const next = segments[i + 1];
        const start = seg.start_sec;
        let end = seg.end_sec ?? next?.start_sec ?? start + MIN_AUDIT_CUE_SEC;
        if (end - start < MIN_AUDIT_CUE_SEC) {
            end = start + MIN_AUDIT_CUE_SEC;
        }
        const text = formatSegmentTitleWithRole(seg.role, seg.title);
        idx += 1;
        lines.push(String(idx));
        lines.push(`${vttTimestamp(start)} --> ${vttTimestamp(end)}`);
        lines.push(vttEscape(text));
        lines.push("");
    }
    return `${lines.join("\n")}\n`;
}

function formatAuditTimestamp(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds < 0) {
        return "0:00";
    }
    const total = Math.floor(seconds);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) {
        return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }
    return `${m}:${String(s).padStart(2, "0")}`;
}

function parseAuditSegmentsFromJson(data: unknown): AuditVideoStepSegment[] {
    if (!data || typeof data !== "object") {
        return [];
    }
    const steps = (data as { steps?: unknown }).steps;
    if (!Array.isArray(steps)) {
        return [];
    }
    const out: AuditVideoStepSegment[] = [];
    for (const raw of steps) {
        if (!raw || typeof raw !== "object") {
            continue;
        }
        const step = raw as {
            title?: unknown;
            stage?: unknown;
            role?: unknown;
            start_sec?: unknown;
            end_sec?: unknown;
        };
        const title = typeof step.title === "string" ? step.title.trim() : "";
        if (!title) {
            continue;
        }
        const stageRaw = step.stage;
        const stage =
            typeof stageRaw === "string" && stageRaw.trim()
                ? stageRaw.trim()
                : undefined;
        const roleRaw = step.role;
        const role =
            typeof roleRaw === "string" && roleRaw.trim() ? roleRaw.trim() : undefined;
        const start = Number(step.start_sec);
        if (!Number.isFinite(start)) {
            continue;
        }
        const endRaw = step.end_sec;
        const end = endRaw === undefined || endRaw === null ? null : Number(endRaw);
        out.push({
            title,
            stage,
            role,
            start_sec: start,
            end_sec: end !== null && Number.isFinite(end) ? end : null,
        });
    }
    out.sort((a, b) => a.start_sec - b.start_sec);
    return out;
}

/** Preserves stage order by first appearance in the (time-sorted) step list. */
function groupAuditSegmentsByStage(
    segments: AuditVideoStepSegment[]
): { stageLabel: string; segments: AuditVideoStepSegment[] }[] {
    const order: string[] = [];
    const byStage = new Map<string, AuditVideoStepSegment[]>();
    for (const seg of segments) {
        const label = seg.stage?.trim() || "Uncategorized";
        if (!byStage.has(label)) {
            byStage.set(label, []);
            order.push(label);
        }
        byStage.get(label)!.push(seg);
    }
    return order.map((stageLabel) => ({
        stageLabel,
        segments: byStage.get(stageLabel)!,
    }));
}

type DisplayStatus = "Operational" | "Degraded" | "Unavailable" | "Unknown";

function toDisplayStatus(s: CheckStatus): DisplayStatus {
    if (s === "ok") {
        return "Operational";
    }
    if (s === "error") {
        return "Unavailable";
    }
    if (s === "warning" || s === "degraded") {
        return "Degraded";
    }
    return "Unknown";
}

function statusChipColor(
    s: CheckStatus
): "success" | "error" | "warning" | "default" {
    if (s === "ok") {
        return "success";
    }
    if (s === "error") {
        return "error";
    }
    if (s === "warning" || s === "degraded") {
        return "warning";
    }
    return "default";
}

function statusChipIcon(s: CheckStatus) {
    if (s === "ok") {
        return <CheckCircleIcon />;
    }
    if (s === "error") {
        return <ErrorOutlineIcon />;
    }
    if (s === "warning" || s === "degraded") {
        return <WarningAmberIcon />;
    }
    return <HelpOutlineIcon />;
}


const QualityAssurance: React.FC = () => {
    const auditVideoRef = useRef<HTMLVideoElement | null>(null);
    const [health, setHealth] = useState<HealthPayload | null>(null);
    const [healthLoading, setHealthLoading] = useState(true);
    const [healthError, setHealthError] = useState<string | null>(null);

    const [auditSegments, setAuditSegments] = useState<AuditVideoStepSegment[]>([]);
    const [auditSegmentsLoading, setAuditSegmentsLoading] = useState(true);
    const [auditSegmentsError, setAuditSegmentsError] = useState<string | null>(null);
    const [auditVttBlobUrl, setAuditVttBlobUrl] = useState<string | null>(null);

    const seekAuditVideo = useCallback((seconds: number) => {
        const el = auditVideoRef.current;
        if (!el) {
            return;
        }
        let t = Math.max(0, seconds);
        if (Number.isFinite(el.duration) && el.duration > 0) {
            t = Math.min(t, el.duration);
        }
        el.currentTime = t;
        void el.play().catch(() => {
            /* user may have blocked autoplay; seek still applied */
        });
    }, []);

    const fetchHealth = useCallback(async () => {
        const base = resolveApiBaseURL().replace(/\/$/, "");
        const url = `${base}/api/health`;
        try {
            const res = await fetch(url, { credentials: "omit" });
            if (!res.ok) {
                setHealthError("Service status could not be loaded.");
                setHealth(null);
                return;
            }
            const data = (await res.json()) as HealthPayload;
            setHealth(data);
            setHealthError(null);
        } catch {
            setHealthError("Service status could not be loaded.");
            setHealth(null);
        } finally {
            setHealthLoading(false);
        }
    }, []);

    useEffect(() => {
        void fetchHealth();
        const id = window.setInterval(() => void fetchHealth(), POLL_MS);
        return () => window.clearInterval(id);
    }, [fetchHealth]);

    useEffect(() => {
        let cancelled = false;
        setAuditSegmentsLoading(true);
        setAuditSegmentsError(null);
        void (async () => {
            try {
                const res = await fetch(E2E_AUDIT_VIDEO_JSON_URL, {
                    mode: "cors",
                    credentials: "omit",
                });
                if (!res.ok) {
                    throw new Error(`Could not load audit steps (${res.status})`);
                }
                const data = (await res.json()) as unknown;
                const segments = parseAuditSegmentsFromJson(data);
                if (!cancelled) {
                    setAuditSegments(segments);
                }
            } catch {
                if (!cancelled) {
                    setAuditSegments([]);
                    setAuditSegmentsError("Could not load audit step list.");
                }
            } finally {
                if (!cancelled) {
                    setAuditSegmentsLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!auditSegments.length) {
            setAuditVttBlobUrl(null);
            return;
        }
        const body = buildWebVttFromSegments(auditSegments);
        const blob = new Blob([body], { type: "text/vtt" });
        const url = URL.createObjectURL(blob);
        setAuditVttBlobUrl(url);
        return () => {
            URL.revokeObjectURL(url);
        };
    }, [auditSegments]);

    const serviceRows = useMemo(() => health?.checks ? health.checks : [], [health]);

    const auditSegmentsByStage = useMemo(
        () => groupAuditSegmentsByStage(auditSegments),
        [auditSegments]
    );

    return (
        <Container maxWidth="lg" sx={{ py: 8 }}>
            <Box sx={{ mb: 6, textAlign: "center" }}>
                <Typography variant="h3" component="h1" gutterBottom fontWeight="700">
                    Quality Assurance & System Integrity
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: "auto" }}>
                    At Strong Mind Strong Performance, our commitment to human development is matched by our
                    dedication to technical excellence and user safety.
                </Typography>
                {health?.timestamp ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Last checked: {new Date(health.timestamp).toLocaleString()} · refreshes every{" "}
                        {POLL_MS / 1000}s
                    </Typography>
                ) : null}
            </Box>

            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                Real-Time System Health
            </Typography>

            {healthLoading && !health && <LinearProgress sx={{ mb: 2 }} />}
            {healthError ? (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    {healthError}
                </Alert>
            ) : null}

            <TableContainer component={Paper} elevation={2} sx={{ mb: 6, borderRadius: 2 }}>
                <Table>
                    <TableHead sx={{ bgcolor: "action.hover" }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>Service Layer</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {health && serviceRows.length > 0
                            ? serviceRows.map((row: HealthCheckItem) => (
                                <TableRow key={row.label}>
                                    <TableCell sx={{ fontWeight: 500 }}>{row.label}</TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={statusChipIcon(row.status)}
                                            label={toDisplayStatus(row.status)}
                                            color={statusChipColor(row.status)}
                                            variant="outlined"
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary" display="block">
                                            {row.status}
                                        </Typography>
                                        {row.detail ? (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                display="block"
                                                sx={{ mt: 0.5 }}
                                            >
                                                {row.detail}
                                            </Typography>
                                        ) : null}
                                    </TableCell>
                                </TableRow>
                            ))
                            : null}
                        {!health && !healthLoading ? (
                            <TableRow>
                                <TableCell colSpan={3}>
                                    <Typography variant="body2" color="text.secondary">
                                        No health data yet.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : null}
                        {healthLoading && !health ? (
                            <TableRow>
                                <TableCell colSpan={3}>
                                    <Typography variant="body2" color="text.secondary">
                                        Loading system health…
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : null}
                    </TableBody>
                </Table>
            </TableContainer>

            <Divider sx={{ my: 6 }} />

            <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    End-to-End Testing & Verification
                </Typography>
                <Typography variant="body1" paragraph color="text.secondary">
                    Our E2E Testing Suite mimics real-world user behavior across every possible role. Tests verify permissions security, ARIA compliance, and overall functionality.
                    Below is
                    a recent simulator integrity audit recording.
                </Typography>
            </Box>

            <Grid container spacing={1} sx={{ justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}
            >
                <Grid sx={{ minWidth: '375px' }} >
                    <video
                        ref={auditVideoRef}
                        src={E2E_AUDIT_VIDEO_URL}
                        crossOrigin="anonymous"
                        controls
                        poster={"https://storage.googleapis.com/strongmsp-media/media/simulator/mobile-qa-poster.png"}
                        playsInline
                        preload="metadata"
                        style={{
                            width: "100%",
                            maxWidth: "375px",
                            height: "100%",
                            display: "block",
                            objectFit: "contain",
                        }}
                    >
                        {auditVttBlobUrl ? (
                            <track
                                key={auditVttBlobUrl}
                                kind="captions"
                                srcLang="en"
                                label="English"
                                src={auditVttBlobUrl}
                                default
                            />
                        ) : null}
                    </video>
                </Grid>
                <Grid sx={{ height: '667px', overflowY: 'scroll', px: 1 }} >
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Audit Timeline
                    </Typography>
                    {auditSegmentsError ? (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {auditSegmentsError}
                        </Alert>
                    ) : null}
                    {auditSegmentsLoading ? (
                        <LinearProgress sx={{ mb: 2 }} />
                    ) : null}
                    <List dense>
                        {auditSegmentsByStage.map((group, groupIndex) => {
                            const isLastGroup = groupIndex === auditSegmentsByStage.length - 1;
                            return (
                                <React.Fragment key={group.stageLabel}>
                                    <ListSubheader
                                        disableSticky
                                        sx={{
                                            py: 1,
                                            px: 0,
                                            lineHeight: 1.3,
                                            bgcolor: "transparent",
                                        }}
                                    >
                                        <Typography variant="overline" color="text.secondary" fontWeight={700}>
                                            {group.stageLabel}
                                        </Typography>
                                    </ListSubheader>
                                    {group.segments.map((seg, indexInGroup) => {
                                        const timeLabel =
                                            seg.end_sec !== null
                                                ? `${formatAuditTimestamp(seg.start_sec)} – ${formatAuditTimestamp(seg.end_sec)}`
                                                : formatAuditTimestamp(seg.start_sec);
                                        const primaryLabel = formatSegmentTitleWithRole(seg.role, seg.title);
                                        const isLastInGroup = indexInGroup === group.segments.length - 1;
                                        const showDivider = !(isLastGroup && isLastInGroup);
                                        return (
                                            <ListItem
                                                key={`${group.stageLabel}-${seg.start_sec}-${indexInGroup}`}
                                                disablePadding
                                                divider={showDivider}
                                            >
                                                <ListItemButton
                                                    onClick={() => seekAuditVideo(seg.start_sec)}
                                                    aria-label={`Jump video to ${timeLabel}: ${primaryLabel}`}
                                                >
                                                    <ListItemText
                                                        primary={primaryLabel}
                                                        secondary={timeLabel}
                                                        primaryTypographyProps={{ fontWeight: 600, color: "primary" }}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}
                    </List>
                    {!auditSegmentsLoading && !auditSegmentsError && auditSegments.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            No simulator steps loaded.
                        </Typography>
                    ) : null}
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                        Captions: in-player (WebVTT). Also available as{" "}
                        <Link href={E2E_AUDIT_VIDEO_SRT_URL} download target="_blank" rel="noopener noreferrer">
                            SRT
                        </Link>
                        .
                    </Typography>
                </Grid>
            </Grid>

            {/* <Alert severity="info" sx={{ mt: 6, borderRadius: 2 }}>
                <strong>Safety Protocol:</strong> Our AI content monitoring is a non-negotiable part of our
                stack. If latency exceeds 200ms, our system is designed to fail-safe, ensuring human
                intervention is prioritized.
            </Alert> */}
        </Container >
    );
};

export default QualityAssurance;
